import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAndParseGyms,
  detectBossFromMap,
  TrainerData,
  DetectedBoss,
  gymLeaders,
  e4Members,
} from '../../../shared/utils/bossBattlesData';
import { getPokemonDexUrl } from '../../../shared/utils/pokemonDexUrl';
import './BossSidebarTab.scss';

interface BossSidebarTabProps {
  currentMap?: string | null;
  badgeCount: number;
  onInspectPokemon?: (pokemonName: string) => void;
}

export const BossSidebarTab: React.FC<BossSidebarTabProps> = ({ currentMap, badgeCount, onInspectPokemon }) => {
  const [trainers, setTrainers] = useState<TrainerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active user selections (can be overridden by user or snapped to detected boss)
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedTrainerKey, setSelectedTrainerKey] = useState<string>('');
  const [infoToggles, setInfoToggles] = useState<Record<string, Record<string, boolean>>>({});

  // Live auto-detected boss from game memory
  const detectedBoss: DetectedBoss | null = useMemo(() => {
    return detectBossFromMap(currentMap, badgeCount);
  }, [currentMap, badgeCount]);

  // Load gym boss data
  useEffect(() => {
    let mounted = true;
    fetchAndParseGyms()
      .then((data) => {
        if (!mounted) return;
        setTrainers(data);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // When a boss is detected (or map changes), sync the selection to the detected boss
  useEffect(() => {
    if (detectedBoss) {
      setSelectedStage(detectedBoss.stage);
      setSelectedTrainerKey(detectedBoss.trainerKey);
    } else {
      // Default stage to the player's next expected gym tier (Badges + 1)
      const nextGymNumber = Math.min(Math.max(badgeCount + 1, 1), 8);
      const defaultStage = `gym-${nextGymNumber}`;
      setSelectedStage((prev) => (prev ? prev : defaultStage));
      setSelectedTrainerKey((prev) => (prev ? prev : 'ROXANNE'));
    }
  }, [detectedBoss, badgeCount]);

  const toggleInfo = (trainerId: string, type: string) => {
    setInfoToggles((prev) => ({
      ...prev,
      [trainerId]: {
        ...prev[trainerId],
        [type]: !(prev[trainerId]?.[type] || false),
      },
    }));
  };

  // Filter trainers based on selected stage and trainerKey
  const activeTrainer = useMemo(() => {
    if (!trainers.length) return null;

    // Search for exact match
    const stageToFind = selectedStage || `gym-${Math.min(badgeCount + 1, 8)}`;
    const keyToFind = selectedTrainerKey || 'ROXANNE';

    return trainers.find((t) => {
      const matchStage = t.stage === stageToFind;
      if (!matchStage) return false;

      const normTrainer = t.trainerKey.replace(/ & /g, '&').toUpperCase();
      const normKey = keyToFind.replace(/ & /g, '&').toUpperCase();
      return normTrainer === normKey || normTrainer.includes(normKey) || normKey.includes(normTrainer);
    }) || trainers.find((t) => t.stage === stageToFind) || trainers[0];
  }, [trainers, selectedStage, selectedTrainerKey, badgeCount]);

  // Options for available trainers at the current stage
  const availableTrainersForStage = useMemo(() => {
    if (!selectedStage) return [];
    if (selectedStage.startsWith('gym')) {
      const list = [...gymLeaders];
      const match = selectedStage.match(/gym-(\d+)/);
      if (match && match[1] === '8') {
        list.push('ARCHIE', 'MAXIE');
      }
      return list;
    }
    if (selectedStage === 'e4') {
      return [...e4Members];
    }
    if (selectedStage === 'champion') {
      return ['STEVEN', 'WALLY'];
    }
    return [];
  }, [selectedStage]);

  const isCurrentGymDetected = Boolean(
    detectedBoss &&
    detectedBoss.stage === selectedStage &&
    (detectedBoss.trainerKey === selectedTrainerKey ||
     detectedBoss.trainerKey.includes(selectedTrainerKey) ||
     selectedTrainerKey.includes(detectedBoss.trainerKey))
  );

  return (
    <div className="boss-sidebar-tab">
      {/* ── Auto-Detection Header Banner ── */}
      {detectedBoss ? (
        <div className="detected-boss-banner">
          <div className="detected-badge-tag">
            <span className="live-pulse"></span>
            LIVE DETECTED
          </div>
          <div className="detected-boss-title">
            <span className="location-name">{detectedBoss.displayName}</span>
            <span className="stage-tag">
              {detectedBoss.isGym ? `Gym ${detectedBoss.gymNumber} (Tier ${detectedBoss.gymNumber})` : 'League Boss'}
            </span>
          </div>
          <div className="detected-subtitle">
            Map: <code className="map-code">{detectedBoss.mapName.replace(/_/g, ' ')}</code>
          </div>
          {!isCurrentGymDetected && (
            <button
              className="jump-to-detected-btn"
              onClick={() => {
                setSelectedStage(detectedBoss.stage);
                setSelectedTrainerKey(detectedBoss.trainerKey);
              }}
            >
              Back to {detectedBoss.displayName}
            </button>
          )}
        </div>
      ) : (
        <div className="upcoming-tier-banner">
          <span className="tier-label">Next Gym Tier</span>
          <span className="tier-value">
            Gym {Math.min(badgeCount + 1, 8)} ({badgeCount} {badgeCount === 1 ? 'badge' : 'badges'})
          </span>
        </div>
      )}

      {/* ── Stage Selector Pills ── */}
      <div className="boss-stage-pills-wrapper">
        <div className="boss-stage-pills">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => {
            const st = `gym-${num}`;
            const isTargetTier = badgeCount + 1 === num;
            const isSelected = selectedStage === st;
            return (
              <button
                key={st}
                className={`stage-pill ${isSelected ? 'active' : ''} ${isTargetTier ? 'target-tier' : ''}`}
                onClick={() => setSelectedStage(st)}
                title={`Gym ${num}${isTargetTier ? ' (Your Next Badge)' : ''}`}
              >
                G{num}
              </button>
            );
          })}
          <button
            className={`stage-pill ${selectedStage === 'e4' ? 'active' : ''}`}
            onClick={() => setSelectedStage('e4')}
            title="Elite Four"
          >
            E4
          </button>
          <button
            className={`stage-pill ${selectedStage === 'champion' ? 'active' : ''}`}
            onClick={() => setSelectedStage('champion')}
            title="Champion (Steven / Wally)"
          >
            Champ
          </button>
        </div>
      </div>

      {/* ── Trainer Selector Chips ── */}
      <div className="boss-trainer-selector-wrapper">
        <select
          className="boss-trainer-dropdown"
          value={selectedTrainerKey}
          onChange={(e) => setSelectedTrainerKey(e.target.value)}
        >
          {availableTrainersForStage.map((key) => {
            const formatted = key.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
            return (
              <option key={key} value={key}>
                {formatted} {selectedStage.startsWith('gym') ? `(${selectedStage.replace('gym-', 'Gym ')})` : ''}
              </option>
            );
          })}
        </select>
      </div>

      {/* ── Content View ── */}
      {loading && (
        <div className="boss-tab-loading">
          <span className="loading-spinner"></span>
          Loading boss battle documentation...
        </div>
      )}

      {error && <div className="boss-tab-error">Failed to load boss data: {error}</div>}

      {!loading && !error && activeTrainer && (
        <div className="boss-trainer-content">
          {/* Trainer Summary Card */}
          <div
            className="boss-header-card"
            style={{ borderLeftColor: activeTrainer.color || '#4ade80' }}
          >
            <div className="boss-name-row">
              <span className="boss-name" style={{ color: activeTrainer.color || '#fff' }}>
                {activeTrainer.displayName}
              </span>
              {activeTrainer.items && (
                <span className="boss-items-tag">🎒 {activeTrainer.items}</span>
              )}
            </div>

            {/* Blitz Mechanic & Boss Tips */}
            {activeTrainer.infoSections.map((sec, idx) => (
              <div
                key={idx}
                className={`boss-info-callout ${sec.type}`}
                style={{ marginTop: '0.4rem' }}
              >
                <div className="callout-header">
                  {sec.type === 'blitzMechanic' ? '⚡ Blitz Mechanic' : '💡 Boss Tip'}
                </div>
                <div className="callout-body">{sec.content}</div>
              </div>
            ))}

            {activeTrainer.generalInfo.length > 0 && (
              <div className="boss-general-info">
                {activeTrainer.generalInfo.map((info, idx) => (
                  <div key={idx} className="general-info-item">{info}</div>
                ))}
              </div>
            )}
          </div>

          {/* Pokémon Team List */}
          <div className="boss-pokemon-list">
            <div className="boss-team-heading">
              <span>Enemy Team ({activeTrainer.pokemon.length})</span>
              <a
                href={`/BossBattles`}
                target="_blank"
                rel="noopener noreferrer"
                className="full-docs-link"
                title="Open Boss Battles documentation page"
              >
                Full Docs ↗
              </a>
            </div>

            {activeTrainer.pokemon.map((mon, idx) => (
              <div key={idx} className="boss-mon-compact-card">
                <div className="mon-main-info">
                  <img
                    src={mon.imageUrl}
                    alt={mon.name}
                    className="mon-sprite"
                    onError={(e) => {
                      // Fallback to local mini icon if home sprite fails
                      e.currentTarget.src = `/MiniIcons/${mon.name}.png`;
                      e.currentTarget.className = 'mon-sprite pixelated';
                    }}
                  />
                  <div className="mon-text-details">
                    <div className="mon-name-line">
                      <a
                        href={getPokemonDexUrl(mon.name)}
                        className="boss-mon-link"
                        title={onInspectPokemon ? `Look up ${mon.name} in Pokédex sidebar (Ctrl+Click for new tab)` : `Open ${mon.name} in Pokédex`}
                        style={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={(e) => {
                          if (!e.ctrlKey && !e.metaKey && e.button === 0 && onInspectPokemon) {
                            e.preventDefault();
                            onInspectPokemon(mon.name);
                          }
                        }}
                      >
                        <span className="mon-name">{mon.name} <span style={{ fontSize: '0.7em', opacity: 0.6 }}>📖</span></span>
                      </a>
                      {mon.gender === 'M' && <span className="gender-m">♂</span>}
                      {mon.gender === 'F' && <span className="gender-f">♀</span>}
                      {mon.level && <span className="mon-level">Lv. {mon.level}</span>}
                    </div>
                    {mon.item && (
                      <div className="mon-item">
                        <span className="item-icon">@</span> {mon.item}
                      </div>
                    )}
                    {mon.ability && (
                      <div className="mon-ability">
                        Ability: <span className="ability-name">{mon.ability}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Move Grid */}
                {mon.moves && mon.moves.length > 0 && (
                  <div className="mon-moves-grid">
                    {mon.moves.map((move, mIdx) => {
                      const moveUrlName = move.toLowerCase().replace(/ /g, '-');
                      return (
                        <a
                          key={mIdx}
                          href={`https://pokemondb.net/move/${moveUrlName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="move-chip"
                        >
                          {move}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BossSidebarTab;
